import { createMuiTheme } from '@material-ui/core/styles';

const mid = {
  bg0:     '#020617',
  bg1:     '#0f172a',
  bg2:     '#1e293b',
  bg3:     '#334155',
  border:  '#1e293b',

  blue:    '#3b82f6',
  cyan:    '#06b6d4',
  indigo:  '#6366f1',
  teal:    '#14b8a6',

  text0:   '#e5e7eb',
  text1:   '#94a3b8',
  text2:   '#64748b',

  red:     '#f43f5e',
  amber:   '#f59e0b',
  green:   '#22c55e',
};

const theme = createMuiTheme({
  typography: {
    useNextVariants: true,
    fontSize: 13,
    fontFamily: '"Inter", "SF Pro Display", system-ui, -apple-system, sans-serif',
    h6: { fontWeight: 600, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 600, fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase' },
    body2: { fontSize: '13px', lineHeight: 1.6 },
    caption: { fontSize: '11px', color: mid.text1 },
  },

  palette: {
    type: 'dark',
    primary: {
      main:         mid.blue,
      light:        mid.cyan,
      dark:         mid.indigo,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main:         mid.cyan,
      light:        mid.teal,
      dark:         mid.blue,
      contrastText: '#FFFFFF',
    },
    background: {
      default: mid.bg0,
      paper:   mid.bg1,
    },
    text: {
      primary:   mid.text0,
      secondary: mid.text1,
      disabled:  mid.text2,
      hint:      mid.text2,
    },
    divider: mid.border,
    error:   { main: mid.red },
    warning: { main: mid.amber },
    success: { main: mid.green },
  },

  overrides: {
    MuiAppBar: {
      colorPrimary: {
        backgroundColor: '#030712',
        color: mid.text0,
        borderBottom: `1px solid ${mid.border}`,
        boxShadow: 'none',
      },
    },

    MuiDrawer: {
      paper: {
        backgroundColor: '#030712',
        borderRight: `1px solid ${mid.border}`,
      },
    },

    MuiListItem: {
      root: {
        borderRadius: '6px',
        margin: '2px 8px',
        width: 'auto',
        '&:hover': { backgroundColor: 'rgba(59,130,246,0.08)' },
        '&$selected': {
          backgroundColor: 'rgba(59,130,246,0.12)',
          borderLeft: `2px solid ${mid.blue}`,
        },
      },
    },

    MuiListItemIcon: {
      root: {
        color: mid.text2,
        minWidth: '36px',
      },
    },

    MuiTableHead: {
      root: {
        backgroundColor: mid.bg2,
      },
    },

    MuiTableCell: {
      head: {
        color: mid.cyan,
        fontWeight: 600,
        fontSize: '11px',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        borderBottom: `1px solid ${mid.border}`,
        padding: '10px 16px',
        backgroundColor: mid.bg2,
      },
      body: {
        color: mid.text0,
        borderBottom: `1px solid ${mid.border}`,
        fontSize: '13px',
        padding: '10px 16px',
      },
    },

    MuiTableRow: {
      root: {
        backgroundColor: mid.bg1,
        transition: 'background-color 0.15s ease',
        '&:hover': {
          backgroundColor: 'rgba(59,130,246,0.06)',
        },
      },
    },

    MuiPaper: {
      root: {
        backgroundColor: mid.bg1,
        color: mid.text0,
      },
      rounded: {
        borderRadius: '8px',
      },
      elevation1: {
        boxShadow: `0 1px 3px rgba(0,0,0,0.4), 0 0 0 1px ${mid.border}`,
      },
      elevation2: {
        boxShadow: `0 4px 12px rgba(0,0,0,0.5), 0 0 0 1px ${mid.border}`,
      },
    },

    MuiCard: {
      root: {
        backgroundColor: mid.bg1,
        border: `1px solid ${mid.border}`,
        borderRadius: '8px',
        boxShadow: 'none',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          borderColor: 'rgba(59,130,246,0.3)',
          boxShadow: '0 0 0 1px rgba(59,130,246,0.1)',
        },
      },
    },

    MuiCardHeader: {
      root: {
        borderBottom: `1px solid ${mid.border}`,
        padding: '14px 20px',
      },
      title: {
        fontSize: '14px',
        fontWeight: 600,
        color: mid.text0,
      },
      subheader: {
        fontSize: '12px',
        color: mid.text1,
      },
    },

    MuiButton: {
      root: {
        textTransform: 'none',
        fontSize: '13px',
        fontWeight: 500,
        borderRadius: '6px',
        padding: '6px 16px',
      },
      contained: {
        boxShadow: 'none',
        '&:hover': { boxShadow: 'none' },
      },
      containedPrimary: {
        background: `linear-gradient(135deg, ${mid.blue} 0%, ${mid.indigo} 100%)`,
        '&:hover': {
          background: `linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)`,
        },
      },
      outlined: {
        borderColor: mid.border,
        '&:hover': {
          borderColor: mid.blue,
          backgroundColor: 'rgba(59,130,246,0.06)',
        },
      },
    },

    MuiChip: {
      root: {
        fontSize: '11px',
        fontWeight: 500,
        borderRadius: '4px',
        height: '22px',
        backgroundColor: mid.bg3,
        color: mid.text1,
      },
      colorPrimary: {
        backgroundColor: 'rgba(59,130,246,0.15)',
        color: mid.blue,
      },
    },

    MuiInputBase: {
      root: {
        color: mid.text0,
        fontSize: '13px',
      },
    },

    MuiOutlinedInput: {
      root: {
        borderRadius: '6px',
        '& $notchedOutline': { borderColor: mid.border },
        '&:hover $notchedOutline': { borderColor: mid.blue },
        '&$focused $notchedOutline': { borderColor: mid.blue },
      },
    },

    MuiSelect: {
      root: { color: mid.text0 },
      icon: { color: mid.text1 },
    },

    MuiMenuItem: {
      root: {
        fontSize: '13px',
        '&:hover': { backgroundColor: 'rgba(59,130,246,0.08)' },
        '&$selected': { backgroundColor: 'rgba(59,130,246,0.12)' },
      },
    },

    MuiDivider: {
      root: { backgroundColor: mid.border },
    },

    MuiTooltip: {
      tooltip: {
        backgroundColor: mid.bg3,
        border: `1px solid ${mid.border}`,
        fontSize: '12px',
        color: mid.text0,
        borderRadius: '6px',
      },
    },

    MuiLinearProgress: {
      root: { borderRadius: '2px' },
      colorPrimary: { backgroundColor: mid.bg3 },
      barColorPrimary: {
        background: `linear-gradient(90deg, ${mid.blue}, ${mid.cyan})`,
      },
    },

    MuiCircularProgress: {
      colorPrimary: { color: mid.blue },
    },

    MuiBadge: {
      colorPrimary: { backgroundColor: mid.blue },
    },

    MuiTab: {
      root: {
        textTransform: 'none',
        fontSize: '13px',
        fontWeight: 500,
        minWidth: '80px',
      },
      selected: { color: mid.blue },
    },

    MuiTabs: {
      indicator: {
        backgroundColor: mid.blue,
        height: '2px',
      },
    },

    MuiDialog: {
      paper: {
        backgroundColor: mid.bg1,
        border: `1px solid ${mid.border}`,
        borderRadius: '12px',
      },
    },
  },
});

export { mid };
export default theme;



// WEBPACK FOOTER //
// ./src/theme.js